import { useMemo } from "react";

import { getFieldError, isValidDate } from "@shared";
import { startOfDay } from "date-fns";
import { FormikProps, FormikValues } from "formik";

import { nameof } from "@/common/helper/commonHelper";
import { ExchangeAppointmentSelectionValues } from "@/pages/exchange/Exchange.types";

import { ExchangeableRange, useExchangeableAppointmentsQuery } from "./useExchangeableAppointmentsQuery";

/**
 * The date range and single-Termin selection shared by the Neue-Anfrage and the Angebot-abgeben
 * dialog: which range is complete enough to query, when the picker should explain itself instead of
 * listing, and keeping the selection in step with the dates.
 */
export function useExchangeableAppointmentSelection<T extends ExchangeAppointmentSelectionValues & FormikValues>(
  formik: FormikProps<T>
) {
  const { from, till, appointmentId } = formik.values;

  // Only a complete, ordered range is requested; the picker explains the other states itself.
  const range = useMemo<ExchangeableRange | null>(() => {
    if (!isValidDate(from) || !isValidDate(till)) {
      return null;
    }
    const fromDay = startOfDay(from);
    const tillDay = startOfDay(till);
    return tillDay >= fromDay ? { from: fromDay, till: tillDay } : null;
  }, [from, till]);

  const datesSelected = isValidDate(from) && isValidDate(till);
  const rangeValid = !datesSelected || range !== null;

  const { appointments, isLoading, isError } = useExchangeableAppointmentsQuery(range);

  // Nothing is validated until the first submit attempt (getFieldError ignores `touched`, so
  // validating earlier would redden fields the user has not reached yet). Once they have tried,
  // every change re-validates so a section stops being marked the moment it is fixed.
  const revalidate = formik.submitCount > 0;
  // Named off the base interface: every T carries this field under the same name.
  const appointmentIdField = nameof<ExchangeAppointmentSelectionValues>("appointmentId");

  const toggleAppointment = (id: string) =>
    void formik.setFieldValue(appointmentIdField, appointmentId === id ? null : id, revalidate);

  // A Termin picked under the previous range may no longer be listed, so the selection follows the
  // dates. Re-validating here also clears the date fields' own marks once they are filled in.
  const clearAppointment = () => void formik.setFieldValue(appointmentIdField, null, revalidate);

  return {
    appointments,
    isLoading,
    isError,
    datesSelected,
    rangeValid,
    toggleAppointment,
    clearAppointment,
    appointmentError: getFieldError(formik, appointmentIdField),
  };
}
